/**
 * Authorization Context
 * 
 * Provides group-based authorization state and methods throughout the application.
 * Extracts user groups from JWT token claims and provides convenient access control methods.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getTokens } from '../utils/tokenStorage';
import type { SOWApprovalStage } from '../types';

/**
 * User groups supported by the system
 */
export enum UserGroup {
  USERS = 'Users',
  ADMINS = 'Admins',
  TECHNICAL_APPROVERS = 'TechnicalApprovers',
  OPERATIONS_APPROVERS = 'OperationsApprovers',
  SALES_APPROVERS = 'SalesApprovers',
  CONTENT_MANAGERS = 'ContentManagers',
}

/**
 * Groups permitted on the routes an approver must be able to reach to do their
 * job: the dashboard (landing page), the approvals queue, and an LOE detail
 * page (where the SOW tab / approval panel lives). Approver-only accounts are a
 * supported server-side persona, so the SPA must not lock them out; every
 * owner-only action inside those pages is separately 403-gated server-side.
 */
export const APPROVER_ACCESSIBLE_ROUTE_GROUPS: string[] = [
  UserGroup.USERS,
  UserGroup.ADMINS,
  UserGroup.TECHNICAL_APPROVERS,
  UserGroup.OPERATIONS_APPROVERS,
  UserGroup.SALES_APPROVERS,
];

/**
 * Maps each SOW approval stage to the Cognito group whose members may approve it.
 */
const STAGE_GROUP: Record<SOWApprovalStage, UserGroup> = {
  TECHNICAL: UserGroup.TECHNICAL_APPROVERS,
  OPERATIONS: UserGroup.OPERATIONS_APPROVERS,
  SALES: UserGroup.SALES_APPROVERS,
};

/**
 * Authorization context type
 */
export interface AuthorizationContextType {
  groups: string[];
  isAdmin: boolean;
  isUser: boolean;
  hasGroup: boolean;
  canAccess: (requiredGroups: string[]) => boolean;
  canApproveStage: (stage: SOWApprovalStage) => boolean;
  /** May upload, edit and delete Sales Assets (ContentManagers or Admins). Server re-checks. */
  canManageAssets: boolean;
  refreshGroups: () => Promise<void>;
  /**
   * Current user's Cognito `sub`, decoded from the id token. Null when
   * unavailable (e.g. no token yet, or decoding fails).
   */
  userId: string | null;
  /**
   * True while groups are being (re-)extracted from the JWT token for the
   * current authentication state. Consumers (e.g. ProtectedRoute) must wait
   * for this to become false before evaluating `hasGroup`/`canAccess`,
   * otherwise a brief window right after auth resolves (e.g. on page
   * refresh) can be mistaken for "user has no groups".
   */
  groupsLoading: boolean;
}

/**
 * Create the authorization context
 */
const AuthorizationContext = createContext<AuthorizationContextType | undefined>(undefined);

/**
 * Authorization Provider Props
 */
interface AuthorizationProviderProps {
  children: React.ReactNode;
}

/**
 * Decode JWT token payload without verification
 * (Verification is done by API Gateway, this is just for reading claims)
 */
function decodeJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Extract groups from ID token
 */
function extractGroupsFromToken(): string[] {
  try {
    const tokens = getTokens();
    if (!tokens || !tokens.idToken) {
      return [];
    }

    const payload = decodeJWT(tokens.idToken);
    if (!payload) {
      return [];
    }

    // Extract cognito:groups claim
    const groupsClaim = payload['cognito:groups'];
    if (!groupsClaim) {
      return [];
    }

    // Handle different formats: array, string with brackets, or comma-separated string
    let groups: string[];
    if (Array.isArray(groupsClaim)) {
      groups = groupsClaim;
    } else if (typeof groupsClaim === 'string') {
      // Handle JSON array string (e.g., "[Admins]" or "[Users,Admins]")
      if (groupsClaim.startsWith('[') && groupsClaim.endsWith(']')) {
        try {
          // Try parsing as JSON first
          groups = JSON.parse(groupsClaim);
        } catch {
          // If JSON parsing fails, manually extract values between brackets
          const content = groupsClaim.slice(1, -1).trim();
          if (content) {
            groups = content.split(',').map(g => g.trim());
          } else {
            groups = [];
          }
        }
      } else {
        // Handle comma-separated string without brackets
        groups = groupsClaim.split(',').map(g => g.trim());
      }
    } else {
      return [];
    }

    return groups;
  } catch (error) {
    console.error('Failed to extract groups from token:', error);
    return [];
  }
}

/**
 * Extract the current user's `sub` claim from the ID token
 */
function extractUserIdFromToken(): string | null {
  try {
    const tokens = getTokens();
    if (!tokens?.idToken) return null;
    const payload = decodeJWT(tokens.idToken);
    return payload?.sub ?? null;
  } catch {
    return null;
  }
}

/**
 * Authorization Provider Component
 * 
 * Manages authorization state and provides group-based access control methods.
 * Automatically extracts groups from JWT token on auth state changes.
 */
export const AuthorizationProvider: React.FC<AuthorizationProviderProps> = ({ children }) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  // Initialize groups synchronously by extracting from token during component initialization
  // This ensures groups are available before the first render of child components
  const [groups, setGroups] = useState<string[]>(() => extractGroupsFromToken());
  // Groups start "loading" until the effect below confirms the extraction
  // reflects the current auth state. Consumers must not treat an empty
  // `groups` array as "no group membership" while this is true.
  const [groupsLoading, setGroupsLoading] = useState<boolean>(true);
  // Current user's Cognito sub, initialized synchronously alongside groups.
  const [userId, setUserId] = useState<string | null>(() => extractUserIdFromToken());

  /**
   * Extract and update groups from current token
   */
  const refreshGroups = useCallback(async (): Promise<void> => {
    setGroupsLoading(true);
    const extractedGroups = extractGroupsFromToken();
    setGroups(extractedGroups);
    setUserId(extractUserIdFromToken());
    setGroupsLoading(false);
  }, []);

  /**
   * Update groups when authentication state changes
   * Only clear groups when authentication is complete and user is not authenticated
   * Don't clear during loading to avoid race condition
   */
  useEffect(() => {
    if (isAuthenticated && user) {
      refreshGroups();
    } else if (!isLoading && !isAuthenticated) {
      // Only clear groups after loading completes and user is confirmed not authenticated
      setGroups([]);
      setUserId(null);
      setGroupsLoading(false);
    }
  }, [isAuthenticated, user, isLoading, refreshGroups]);

  /**
   * Check if user is an admin
   */
  const isAdmin = groups.includes(UserGroup.ADMINS);

  /**
   * Check if user is in the Users group
   */
  const isUser = groups.includes(UserGroup.USERS);

  /**
   * Check if user has any group assignment
   */
  const hasGroup = groups.length > 0;

  /**
   * Check if user can access resources requiring specific groups
   * Returns true if user belongs to any of the required groups
   */
  const canAccess = useCallback((requiredGroups: string[]): boolean => {
    if (!requiredGroups || requiredGroups.length === 0) {
      return true;
    }
    return requiredGroups.some(group => groups.includes(group));
  }, [groups]);

  /**
   * Check if user can approve a given SOW approval stage.
   * Membership in the stage's dedicated approver group is required. Being an
   * Admin does NOT by itself grant approval authority — an Admin can approve a
   * stage only when also assigned that stage's group (separation of duties).
   */
  const canApproveStage = useCallback(
    (stage: SOWApprovalStage): boolean => groups.includes(STAGE_GROUP[stage]),
    [groups]
  );

  const canManageAssets = isAdmin || groups.includes(UserGroup.CONTENT_MANAGERS);

  const value: AuthorizationContextType = {
    groups,
    isAdmin,
    isUser,
    hasGroup,
    canAccess,
    canApproveStage,
    canManageAssets,
    refreshGroups,
    groupsLoading,
    userId,
  };

  return (
    <AuthorizationContext.Provider value={value}>
      {children}
    </AuthorizationContext.Provider>
  );
};

/**
 * Custom hook to access authorization context
 * 
 * @throws Error if used outside of AuthorizationProvider
 * @returns Authorization context
 */
export const useAuthorization = (): AuthorizationContextType => {
  const context = useContext(AuthorizationContext);
  
  if (context === undefined) {
    throw new Error('useAuthorization must be used within an AuthorizationProvider');
  }
  
  return context;
};
